/**
 * Simple Promise-based Polling Function
 *
 * Polls an async function until a condition is met (checkComplete returns true).
 * Automatically retries on network errors, but stops on other errors.
 *
 * Usage:
 *   const result = await poll({
 *     pollFunction: () => fetchStatus(),
 *     checkComplete: (res) => res.status === 'complete'
 *   });
 */

import type { OnLogEvent } from '../types/logger';

const POLL_INTERVAL = 1500; // ms - time between poll attempts

export interface PollOptions {
  pollFunction: () => Promise<any>; // Function to call on each poll
  checkComplete: (response: any) => boolean; // Returns true when polling should stop
  log?: OnLogEvent;
}

/**
 * Poll until checkComplete returns true or an error occurs
 *
 * - Calls pollFunction repeatedly at POLL_INTERVAL intervals
 * - Stops when checkComplete(response) returns true
 * - Retries automatically on network errors
 * - Stops and rejects on other errors
 */
export const poll = async <T = any>(options: PollOptions): Promise<T> => {
  const { pollFunction, checkComplete, log } = options;

  return new Promise<T>((resolve, reject) => {
    let intervalId: NodeJS.Timeout | null = null;

    const cleanup = () => {
      if (intervalId) {
        clearTimeout(intervalId);
        intervalId = null;
      }
    };

    const doPoll = async () => {
      try {
        const response = await pollFunction();

        // Check if we're done
        if (checkComplete(response)) {
          cleanup();
          resolve(response);
          return;
        }

        // Schedule next poll
        intervalId = setTimeout(doPoll, POLL_INTERVAL);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        // Retry on network errors (connection issues)
        if (errorMessage === 'Failed to fetch' || errorMessage.includes('NetworkError')) {
          log?.('info', 'Payment Processing', 'Network error during polling, retrying', { error });
          intervalId = setTimeout(doPoll, POLL_INTERVAL);
        } else {
          // Other errors (API errors, validation errors, etc.) - stop polling
          cleanup();
          reject(error);
        }
      }
    };

    // Start polling immediately
    doPoll();
  });
};
